import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { db } from '../../db/db'
import { creerIngredient, majIngredient, supprimerIngredient } from '../../db/repo'
import {
  FODMAP_FAMILIES,
  FODMAP_FAMILY_LABELS,
  FODMAP_LEVELS,
  FODMAP_LEVEL_LABELS,
  INGREDIENT_CATEGORIES,
  INGREDIENT_CATEGORY_LABELS,
  type FodmapFamily,
  type FodmapLevel,
  type IngredientCategory,
} from '../../domain/types'
import { BoutonSupprimer, Carte, Champ, Entete } from '../composants'

export function PageIngredient() {
  const { id } = useParams()
  const navigate = useNavigate()
  const edition = Boolean(id)

  const [nom, setNom] = useState('')
  const [categorie, setCategorie] = useState<IngredientCategory>('autre')
  const [niveau, setNiveau] = useState<FodmapLevel>('inconnu')
  const [familles, setFamilles] = useState<FodmapFamily[]>([])
  const [synonymes, setSynonymes] = useState('')
  const [perso, setPerso] = useState(true)

  useEffect(() => {
    if (!id) return
    void db.ingredients.get(id).then((i) => {
      if (!i) return
      setNom(i.nom)
      setCategorie(i.categorie)
      setNiveau(i.niveauFodmap)
      setFamilles(i.famillesFodmap)
      setSynonymes(i.synonymes.join(', '))
      setPerso(i.perso)
    })
  }, [id])

  const basculerFamille = (f: FodmapFamily) =>
    setFamilles((s) => (s.includes(f) ? s.filter((x) => x !== f) : [...s, f]))

  const enregistrer = async () => {
    if (!nom.trim()) return
    const data = {
      nom: nom.trim(),
      categorie,
      niveauFodmap: niveau,
      famillesFodmap: familles,
      synonymes: synonymes
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    }
    if (id) await majIngredient(id, data)
    else await creerIngredient(data)
    navigate('/bibliotheque')
  }

  return (
    <>
      <Entete titre={edition ? "Modifier l'ingrédient" : 'Nouvel ingrédient'} retour />

      <main className="contenu">
        {edition && !perso && (
          <div className="encart encart--info">
            Ingrédient du référentiel livré. Vos modifications sont conservées : une mise à jour
            de l'application ne les écrasera pas.
          </div>
        )}

        <Carte>
          <div className="pile">
            <Champ label="Nom">
              <input type="text" value={nom} onChange={(e) => setNom(e.target.value)} />
            </Champ>

            <Champ label="Catégorie">
              <select
                value={categorie}
                onChange={(e) => setCategorie(e.target.value as IngredientCategory)}
              >
                {INGREDIENT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {INGREDIENT_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </Champ>

            <Champ
              label="Synonymes"
              aide="Séparés par des virgules. Facilitent la recherche à la saisie."
            >
              <input
                type="text"
                value={synonymes}
                onChange={(e) => setSynonymes(e.target.value)}
                placeholder="laitue, batavia"
              />
            </Champ>
          </div>
        </Carte>

        <Carte titre="Classification FODMAP">
          <p className="doux" style={{ marginBottom: 12 }}>
            Sert uniquement à l'analyse agrégée par famille, qui atteint la significativité bien
            plus tôt que l'ingrédient isolé. L'analyse par ingrédient ne dépend pas du tout de ces
            étiquettes.
          </p>

          <div className="champ">
            <span className="champ__label">Niveau</span>
            <div className="puces">
              {FODMAP_LEVELS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className="choix"
                  aria-pressed={niveau === n}
                  onClick={() => setNiveau(n)}
                >
                  {FODMAP_LEVEL_LABELS[n]}
                </button>
              ))}
            </div>
          </div>

          <div className="champ" style={{ marginTop: 14 }}>
            <span className="champ__label">Familles concernées</span>
            <div className="puces">
              {FODMAP_FAMILIES.map((f) => (
                <button
                  key={f}
                  type="button"
                  className="choix"
                  aria-pressed={familles.includes(f)}
                  onClick={() => basculerFamille(f)}
                >
                  {FODMAP_FAMILY_LABELS[f]}
                </button>
              ))}
            </div>
          </div>
        </Carte>

        <button
          type="button"
          className="btn btn--principal btn--large"
          onClick={() => void enregistrer()}
          disabled={!nom.trim()}
        >
          Enregistrer
        </button>

        {id && (
          <BoutonSupprimer
            titre="Supprimer cet ingrédient ?"
            message="S'il apparaît déjà dans un repas ou une recette, il sera archivé plutôt que supprimé, afin de ne pas amputer l'historique ni les analyses."
            onSupprimer={() => {
              void supprimerIngredient(id).then(() => navigate('/bibliotheque'))
            }}
          />
        )}
      </main>
    </>
  )
}
